import { useParams, Link, useNavigate } from 'react-router-dom';
import { FadeIn, StaggerContainer, StaggerItem } from '../components/Animations';
import { getMobWikiUrl } from '../data/wiki_mobs_assets';
import './WikiItemDetail.css';
import './WikiMobDetail.css';

const VIDEO = "https://pub-332780ae20b540038a9ea772f1bf747c.r2.dev/background_vids/vid_1.mp4";

const TIER_COLOR = {
  1: '#1aaf12',
  2: '#d7a00a',
  3: '#c62e13',
  4: '#9f09cd',
};

const SPAWN_PROB = {
  extremely_rare: { label: 'Extremely Rare', color: '#9f09cd' },
  very_rare: { label: 'Very Rare', color: '#c62e13' },
  rare: { label: 'Rare', color: '#d7700a' },
  uncommon: { label: 'Uncommon', color: '#c8b800' },
  common: { label: 'Common', color: '#1aaf12' },
  very_common: { label: 'Very Common', color: '#00c896' },
};

const TIER_LABEL = {
  1: 'Tier 1',
  2: 'Tier 2',
  3: 'Tier 3',
  4: 'Tier 4',
};

const GET_MOB_CATEGORY = (mob) => {
  const t = (mob.mob_type || mob.type || mob.name || '').toLowerCase();
  if (t.includes('zombie') || t.includes('skeleton') || t.includes('drowned') || t.includes('stray') || t.includes('husk') || t.includes('phantom') || t.includes('wither') || t.includes('ghast') || t.includes('revenant') || t.includes('bone') || t.includes('lich') || t.includes('knight') || t.includes('general') || t.includes('grunt') || t.includes('soul') || t.includes('maniac') || t.includes('undead')) return 'Undead';
  if (t.includes('wolf') || t.includes('bee') || t.includes('cow') || t.includes('rabbit') || t.includes('hoglin') || t.includes('piglin') || t.includes('brute') || t.includes('ravager') || t.includes('kraken') || t.includes('bull') || t.includes('wasp') || t.includes('hornet') || t.includes('hog') || t.includes('werewolf') || t.includes('pup') || t.includes('lorax') || t.includes('sprout') || t.includes('hydra') || t.includes('sovereign')) return 'Beast';
  if (t.includes('spider') || t.includes('endermite') || t.includes('silverfish') || t.includes('crawler') || t.includes('scuttler') || t.includes('scratcher') || t.includes('scrambler') || t.includes('spiderling') || t.includes('tarantula')) return 'Arthropod';
  if (t.includes('pillager') || t.includes('vindicator') || t.includes('evoker') || t.includes('vex') || t.includes('goblin') || t.includes('traveler')) return 'Illager';
  if (t.includes('golem') || t.includes('guardian') || t.includes('warden') || t.includes('breeze') || t.includes('shard') || t.includes('ore') || t.includes('bedrock') || t.includes('stone') || t.includes('colossus') || t.includes('titan') || t.includes('behemoth') || t.includes('giant') || t.includes('troll') || t.includes('pillar')) return 'Construct';
  return 'Other';
};

function parseSpawn(raw) {
  try { return JSON.parse(raw || '[]'); } catch { return []; }
}

function formatChance(raw) {
  if (!raw && raw !== 0) return null;
  // Strip any % sign and parse
  const num = parseFloat(String(raw).replace('%', ''));
  if (isNaN(num) || num <= 0) return String(raw);
  const pct = `${num}%`;
  if (num >= 100) return `${pct} (1/1)`;
  const denom = Math.round(100 / num);
  return `${pct} (1/${denom})`;
}

export default function WikiMobDetail({ items = [], mobs = [] }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const mob = mobs.find(m => m.id === parseInt(id) && m.enabled !== 0 && m.enabled !== '0');

  if (!mob) {
    return (
      <div className="container" style={{ padding: '4rem 2rem', textAlign: 'center' }}>
        <h2>Mob not found</h2>
        <Link to="/wiki/mobs" className="btn btn-secondary" style={{ marginTop: '1rem' }}>Back to Mobs</Link>
      </div>
    );
  }

  const mobIcon = getMobWikiUrl(mob.mob_type || mob.type, mob.name);
  const tierColor = TIER_COLOR[mob.tier] || '#9e9e9e';
  const tierLabel = TIER_LABEL[mob.tier] || `Tier ${mob.tier}`;
  const category = GET_MOB_CATEGORY(mob);
  const spawnConditions = parseSpawn(mob.spawn);

  let summonIds = [];
  try { summonIds = JSON.parse(mob.summons || '[]'); } catch { }
  const summonedMobs = summonIds
    .map(sid => mobs.find(m => m.id === parseInt(sid)))
    .filter(Boolean);

  return (
    <div className="wiki-detail-root">
      <div className="wiki-detail-hero">
        <video className="wiki-detail-hero-vid" src={VIDEO} autoPlay muted loop playsInline />
        <div className="wiki-detail-hero-overlay" />
        <FadeIn className="wiki-detail-hero-content container" y={30} duration={0.8}>
          <button className="wiki-back-btn" onClick={() => navigate(-1)}>← Back</button>
          <div className="wiki-detail-title-section">
            <div className="wiki-detail-icon-box wmd-icon-box" style={{ '--tier-color': tierColor }}>
              {mob.icon && mob.icon.startsWith('http') ? (
                <img src={mob.icon} alt={mob.name} style={{ width: 56, height: 56, objectFit: 'contain' }} />
              ) : mobIcon ? (
                <img src={mobIcon} alt={mob.name} style={{ width: 56, height: 56, objectFit: 'contain' }} />
              ) : (
                <span style={{ fontSize: '2.5rem' }}>🐾</span>
              )}
            </div>
            <div>
              <h1 className="wiki-detail-item-name" style={{ color: tierColor }}>{mob.name}</h1>
              <div className="wiki-detail-item-type" style={{ color: tierColor }}>
                {tierLabel}
              </div>
            </div>
          </div>
        </FadeIn>
      </div>

      <StaggerContainer className="container">
        <div className="wiki-detail-grid">

          {/* ── Main column: description + abilities ── */}
          <StaggerItem className="wiki-detail-main">
            {(mob.description || mob.lore) && (
              <div>
                <span className="wiki-detail-label">About</span>
                <div className="wiki-detail-box">
                  {mob.description && (
                    <p style={{ margin: 0, color: 'var(--text-secondary)' }}>{mob.description}</p>
                  )}
                  {mob.lore && (
                    <div className={`wiki-lore-box${mob.description ? ' wiki-lore-box--spaced' : ''}`}>
                      <span className="wiki-lore-line" style={{ fontStyle: 'italic' }}>"{mob.lore}"</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {mob.abilities?.length > 0 && (
              <div>
                <span className="wiki-detail-label">Abilities</span>
                <div className="wiki-ability-list">
                  {mob.abilities.map((ability, idx) => (
                    <div key={idx} className="wiki-ability-card">
                      <div className="wiki-ability-head">
                        <span className="wiki-ability-name">{ability.name}</span>
                        {ability.type && <span className="wiki-ability-type">{ability.type}</span>}
                      </div>
                      {ability.description && (
                        <div className="wiki-ability-desc">{ability.description}</div>
                      )}
                      {ability.cooldown && (
                        <div className="wiki-ability-cooldown">Cooldown: {ability.cooldown}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </StaggerItem>

          {/* ── Sidebar: stats, spawn, drops ── */}
          <StaggerItem className="wiki-detail-sidebar">
            <div>
              <span className="wiki-detail-label">Stats</span>
              <div className="wiki-detail-box">
                <div className="wmd-stat-list">
                  {mob.hp != null && mob.hp !== '' && (
                    <div className="wmd-stat-row">
                      <span className="wmd-stat-key">Health</span>
                      <span className="wmd-stat-val">{mob.hp}</span>
                    </div>
                  )}
                  {mob.damage != null && mob.damage !== '' && (
                    <div className="wmd-stat-row">
                      <span className="wmd-stat-key">Damage</span>
                      <span className="wmd-stat-val">{mob.damage}</span>
                    </div>
                  )}
                  {mob.speed != null && mob.speed !== '' && (
                    <div className="wmd-stat-row">
                      <span className="wmd-stat-key">Speed</span>
                      <span className="wmd-stat-val" style={{ textTransform: 'capitalize' }}>{mob.speed}</span>
                    </div>
                  )}
                  {mob.hp == null && mob.damage == null && mob.speed == null && (
                    <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 'var(--t-sm)' }}>No stats recorded.</p>
                  )}
                </div>
              </div>
            </div>

            <div>
              <span className="wiki-detail-label">Spawn Conditions</span>
              <div className="wiki-detail-box">
                {mob.spawn_probability && SPAWN_PROB[mob.spawn_probability] && (() => {
                  const sp = SPAWN_PROB[mob.spawn_probability];
                  return (
                    <div className="wmd-spawn-row" style={{ marginBottom: spawnConditions.length > 0 ? '0.5rem' : 0 }}>
                      <span className="wmd-spawn-type">Probability</span>
                      <span className="wmd-spawn-prob-badge" style={{ color: sp.color, borderColor: sp.color }}>
                        {sp.label}
                      </span>
                    </div>
                  );
                })()}
                {spawnConditions.length === 0 && !mob.spawn_probability ? (
                  <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 'var(--t-sm)' }}>No spawn conditions recorded.</p>
                ) : (
                  <div className="wmd-spawn-list">
                    {spawnConditions.map((cond, i) => (
                      <div key={i} className="wmd-spawn-row">
                        <span className="wmd-spawn-type">{cond.condition}</span>
                        <span className="wmd-spawn-val">{cond.value}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <span className="wiki-detail-label">Drops</span>
              <div className="wiki-detail-box">
                {mob.drops?.length > 0 ? (
                  <div className="wmd-drop-list">
                    {mob.drops.map((drop, idx) => {
                      const linkedItem = items.find(i =>
                        i.name.toLowerCase() === drop.item_name?.toLowerCase() &&
                        i.enabled !== 0 && i.enabled !== '0'
                      );
                      return (
                        <div key={idx} className="wmd-drop-row">
                          {linkedItem ? (
                            <Link to={`/wiki/item/${linkedItem.id}`} className="wmd-drop-name wmd-drop-link">{drop.item_name}</Link>
                          ) : (
                            <span className="wmd-drop-name">{drop.item_name}</span>
                          )}
                          {drop.chance != null && drop.chance !== '' && (
                            <span className="wmd-drop-chance">{formatChance(drop.chance)}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 'var(--t-sm)' }}>No drops recorded.</p>
                )}
              </div>
            </div>

            {summonedMobs.length > 0 && (
              <div>
                <span className="wiki-detail-label">Linked Mobs</span>
                <div className="wiki-detail-box">
                  <div className="wmd-summons-list">
                    {summonedMobs.map(sm => {
                      const smColor = TIER_COLOR[sm.tier] || '#9e9e9e';
                      return (
                        <Link key={sm.id} to={`/wiki/mob/${sm.id}`} className="wmd-summon-row">
                          <span className="wmd-summon-dot" style={{ background: smColor }} />
                          <span className="wmd-summon-name">{sm.name}</span>
                          <span className="wmd-summon-tier" style={{ color: smColor }}>
                            {TIER_LABEL[sm.tier] || `Tier ${sm.tier}`}
                          </span>
                          <span className="wmd-summon-arrow">›</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </StaggerItem>
        </div>
      </StaggerContainer>
    </div>
  );
}
